import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, ChevronRight, Globe, Bell, User, Shield, Heart, Info, LogOut, Link2, ExternalLink, Search, Gift, Loader2, Flag, BookHeart } from 'lucide-react';
import { initPushNotifications, isPushEnabled, disablePushNotifications } from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [settings, setSettings] = useState({
    pureMode: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem('userSettings');
    if (saved) setSettings(JSON.parse(saved));
    // Check current push status
    isPushEnabled().then(setPushEnabled);
  }, []);

  const togglePush = async (enabled) => {
    setPushLoading(true);
    try {
      if (enabled) {
        const success = await initPushNotifications();
        setPushEnabled(success);
      } else {
        await disablePushNotifications();
        setPushEnabled(false);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('userSettings', JSON.stringify(newSettings));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="sticky top-12 z-40 bg-[#050505] border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')} 
            className="p-2 -ml-2 text-[#A1A1AA] hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-white font-['Unbounded']">Settings</h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-2">
        {/* Language */}
        <Link to="/settings/language" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-white">Language</span>
          </div>
          <div className="flex items-center gap-2 text-[#71717A]">
            <span>EN</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>

        {/* Push Notification */}
        <div className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#A1A1AA]" />
            <div>
              <span className="text-white">Push Notifications</span>
              <p className="text-xs text-[#71717A]">{pushEnabled ? 'Enabled' : 'Get notified about gifts, chats & news'}</p>
            </div>
          </div>
          {pushLoading ? (
            <Loader2 className="w-5 h-5 text-[#E11D48] animate-spin" />
          ) : (
            <Switch
              checked={pushEnabled}
              onCheckedChange={togglePush}
              data-testid="push-notification-toggle"
            />
          )}
        </div>

        {/* Divider */}
        <div className="h-4" />

        {/* Account */}
        <Link to="/settings/account" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-white">Account</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#71717A]" />
        </Link>

        {/* Memory Vault */}
        <Link to="/memory" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5" data-testid="settings-memory-link">
          <div className="flex items-center gap-3">
            <BookHeart className="w-5 h-5 text-[#E11D48]" />
            <div>
              <span className="text-white">Memory Vault</span>
              <p className="text-xs text-[#71717A]">What your characters remember</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#71717A]" />
        </Link>

        {/* Pure Mode */}
        <div className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <div>
              <span className="text-white">Pure Mode</span>
              <p className="text-xs text-[#71717A]">Filter mature content</p>
            </div>
          </div>
          <Switch
            checked={settings.pureMode}
            onCheckedChange={(v) => updateSetting('pureMode', v)}
            data-testid="pure-mode-toggle"
          />
        </div>

        {/* Parental Controls */}
        <Link to="/settings/parental" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-white">Parental Controls</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#71717A]" />
        </Link>

        {/* Moderation (admin/owner only) */}
        {user && (user.role === 'owner' || user.role === 'admin') && (
          <Link to="/moderation" className="flex items-center justify-between p-4 bg-[#E11D48]/5 rounded-xl border border-[#E11D48]/20" data-testid="moderation-link">
            <div className="flex items-center gap-3">
              <Flag className="w-5 h-5 text-[#E11D48]" />
              <div>
                <span className="text-white">Moderation</span>
                <p className="text-xs text-[#71717A]">Review flagged characters</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#E11D48]" />
          </Link>
        )}

        {/* Divider */}
        <div className="h-4" />

        {/* Your Social Links */}
        <Link to="/settings/socials" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-white">Your Social Links</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#71717A]" />
        </Link>

        {/* Quick Links */}
        <div className="flex gap-2 mt-2">
          <Link to="/discover" className="flex-1 flex items-center gap-2 p-3 bg-[#0F1115] rounded-xl border border-white/5">
            <Search className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm text-white">Discover</span>
          </Link>
          <Link to="/rewards" className="flex-1 flex items-center gap-2 p-3 bg-[#0F1115] rounded-xl border border-white/5">
            <Gift className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-sm text-white">Rewards</span>
          </Link>
        </div>

        {/* Divider */}
        <div className="h-4" />

        {/* Community */}
        <div className="space-y-1">
          <p className="text-xs text-[#71717A] font-medium uppercase tracking-wider px-1 mb-2">Join Our Community</p>
          <a
            href="https://discord.gg/5r2ZDSJUY"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5 hover:bg-[#5865F2]/10 transition-colors"
            data-testid="community-discord-link"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#5865F2">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z"/>
              </svg>
              <div>
                <span className="text-white">Discord</span>
                <p className="text-xs text-[#71717A]">Chat with the community</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-[#71717A]" />
          </a>
          <a
            href="https://www.instagram.com/animus.aiofficial"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5 hover:bg-[#E1306C]/10 transition-colors"
            data-testid="community-instagram-link"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#E1306C">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <div>
                <span className="text-white">Instagram</span>
                <p className="text-xs text-[#71717A]">@animus.aiofficial</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-[#71717A]" />
          </a>
        </div>

        {/* About Us */}
        <Link to="/settings/about" className="flex items-center justify-between p-4 bg-[#0F1115] rounded-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-white">About Us</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#71717A]" />
        </Link>

        {/* Spacer */}
        <div className="h-8" />

        {/* Logout Button */}
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full py-6 bg-transparent border border-white/10 text-white hover:bg-white/5 rounded-full text-base"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Log Out
        </Button>
      </div>
    </div>
  );
}
